import type { AnyAction } from '@reduxjs/toolkit';
import { currentDeviceType } from './deviceType';
import { canPlayerChange, layersToWrite, playerLayersFor, resolveSetting } from './resolveSetting';
import { getSettingDefinition, type SettingKey, type SettingValue } from './settingsRegistry';
import {
  layerCoordinate,
  selectSettingLayerValue,
  settingLayerValueSet,
} from './settingsLayersSlice';
import type {
  DeviceType,
  PlayerLayer,
  ResolvedSetting,
  SettingContent,
  SettingContext,
  SettingDefinition,
  SettingLayers,
  SettingLock,
  SettingsState,
  StoreBinding,
} from './settingTypes';

/**
 * The context with the kind of device filled in, for a setting kept per device
 * type (this device's type unless the context names one). Other settings never
 * carry a device type.
 */
export const withDeviceType = (
  definition: Pick<SettingDefinition<unknown>, 'residence'>,
  context: SettingContext,
): SettingContext => {
  if (definition.residence !== 'device_type') {
    const { deviceType: _ignored, ...rest } = context;
    return rest;
  }
  return { ...context, deviceType: context.deviceType ?? currentDeviceType() };
};

const isThisDevice = (deviceType: DeviceType | undefined): boolean =>
  deviceType === undefined || deviceType === currentDeviceType();

/**
 * The binding for one player layer, when today's code has a store the model
 * can use there. A layer without a binding is kept in `settingsLayers`. A
 * device-type setting's bound store holds this device's value only.
 */
export const bindingFor = <T>(
  definition: SettingDefinition<T>,
  layer: PlayerLayer,
  context: SettingContext,
): StoreBinding<T> | undefined => {
  if (!isThisDevice(context.deviceType)) return undefined;
  switch (layer) {
    case 'global':
      return definition.bindings?.global;
    case 'mode':
      return context.mode ? definition.bindings?.mode?.[context.mode] : undefined;
    case 'instance': {
      const { mode, instanceId } = context;
      const binding = mode ? definition.bindings?.instance?.[mode] : undefined;
      if (!binding || !instanceId) return undefined;
      return {
        select: (state) => binding.select(state, instanceId),
        write: (value) => binding.write(value, instanceId),
      };
    }
    default:
      return undefined;
  }
};

const readPlayerLayer = <T>(
  state: SettingsState,
  definition: SettingDefinition<T>,
  layer: PlayerLayer,
  context: SettingContext,
): T | undefined => {
  if (layer !== 'global' && !context.mode) return undefined;
  if (layer === 'instance' && !context.instanceId) return undefined;
  const binding = bindingFor(definition, layer, context);
  const stored = binding
    ? binding.select(state)
    : selectSettingLayerValue(state, layerCoordinate(definition.key, layer, context));
  return stored === undefined ? undefined : definition.parse(stored);
};

/**
 * Every layer's value for a setting, read from today's stores and the kept
 * layers. When the player has no global value yet, a setting that moved from
 * an older store reads it there (`fallback`), so nothing changes on upgrade.
 */
export const readSettingLayers = <T>(
  state: SettingsState,
  definition: SettingDefinition<T>,
  context: SettingContext = {},
  content: SettingContent<T> = {},
): SettingLayers<T> => {
  const resolvedContext = withDeviceType(definition, context);
  const playerLayers = playerLayersFor(definition.scope).reduce<SettingLayers<T>>(
    (layers, layer) => ({
      ...layers,
      [layer]: readPlayerLayer(state, definition, layer, resolvedContext),
    }),
    {},
  );
  const fallback =
    playerLayers.global === undefined && definition.fallback
      ? definition.fallback.select(state, resolvedContext)
      : undefined;
  return {
    ...playerLayers,
    ...(fallback === undefined ? {} : { global: definition.parse(fallback) }),
    authored: content.authored === undefined ? undefined : definition.parse(content.authored),
    lock:
      content.lock === undefined
        ? undefined
        : { ...content.lock, value: definition.parse(content.lock.value) },
  };
};

/** A setting's value and where it came from. `content` is the content's authored value and any lock. */
export const selectResolvedSetting = <K extends SettingKey>(
  state: SettingsState,
  key: K,
  context: SettingContext = {},
  content: SettingContent<SettingValue<K>> = {},
): ResolvedSetting<SettingValue<K>> => {
  const definition = getSettingDefinition(key);
  const resolvedContext = withDeviceType(definition, context);
  return resolveSetting(
    definition,
    readSettingLayers(state, definition, resolvedContext, content),
    resolvedContext.deviceType,
  );
};

/** A setting's value: the cascade's winner. */
export const selectSetting = <K extends SettingKey>(
  state: SettingsState,
  key: K,
  context: SettingContext = {},
  content: SettingContent<SettingValue<K>> = {},
): SettingValue<K> => selectResolvedSetting(state, key, context, content).value;

/** The narrowest layer a player's change goes to, given what the context names. */
export const defaultWriteLayer = (
  definition: Pick<SettingDefinition<unknown>, 'key' | 'scope'>,
  context: SettingContext,
): PlayerLayer => {
  if (definition.scope === 'instance' && context.mode && context.instanceId) return 'instance';
  if (definition.scope !== 'global' && context.mode) return 'mode';
  if (definition.scope === 'global') return 'global';
  throw new Error(`Setting ${definition.key} is ${definition.scope}-scoped: name the mode`);
};

export interface SettingWriteOptions<T> {
  /** The layer to write; the narrowest the context names when not set. */
  layer?: PlayerLayer;
  /** A lock on the node being played: nothing is written while it holds. */
  lock?: SettingLock<T>;
}

/**
 * The actions that make a player's change: the existing action for each layer
 * today's code stores, the kept layer for the rest. A feel value changed for
 * one piece / exercise / session also becomes the mode default, unless it
 * belongs to the piece alone. Nothing is written over a lock. The account
 * sync (`accountSettingsSync`) sends the change to the account store.
 */
export const settingWriteActions = <K extends SettingKey>(
  key: K,
  value: SettingValue<K>,
  context: SettingContext = {},
  options: SettingWriteOptions<SettingValue<K>> = {},
): AnyAction[] => {
  const definition = getSettingDefinition(key);
  if (!canPlayerChange({ lock: options.lock })) return [];
  const resolvedContext = withDeviceType(definition, context);
  const parsed = definition.parse(value);
  return layersToWrite(
    definition,
    options.layer ?? defaultWriteLayer(definition, resolvedContext),
  ).map((target) => {
    const binding = bindingFor(definition, target, resolvedContext);
    return binding
      ? binding.write(parsed)
      : settingLayerValueSet({
          key: definition.key,
          layer: target,
          context: resolvedContext,
          value: parsed,
        });
  });
};
